import {z} from 'zod'
import {
    Experience,
    ExperienceVettingBaseQuestion,
    Itinerary,
    TransportingGuestsMode
} from '@/features/experience-shell/types'

export const ExperienceStepKeyName = z.enum([
    'experience',
    'background',
    'merchandising',
    'location',
    'photos',
    'pricing_discounts',
    'legal'
])
export type ExperienceStepKeyName = z.infer<typeof ExperienceStepKeyName>

const RemapItinerariesForViewOnly = Itinerary.omit({answers: true}).merge(
    z.object({
        answers: z.object({
            title: ExperienceVettingBaseQuestion.extend({
                answer: z.string(),
                previousAnswer: z.string().nullable()
            }),
            description: ExperienceVettingBaseQuestion.extend({
                answer: z.string(),
                previousAnswer: z.string().nullable()
            }),
            improved_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string(),
                previousAnswer: z.string().nullable()
            }).optional(),
            improved_description: ExperienceVettingBaseQuestion.extend({
                answer: z.string(),
                previousAnswer: z.string().nullable()
            }).optional(),
            duration: ExperienceVettingBaseQuestion.extend({
                answer: z.number(),
                previousAnswer: z.number().nullable()
            }).optional()
        })
    })
)
export type RemapItinerariesForViewOnly = z.infer<typeof RemapItinerariesForViewOnly>

export const RemapExperienceForViewOnly = Experience.omit({expertise: true, itineraries: true}).extend({
    expertise: z.object({
        ...Experience.shape['expertise'].omit({answers: true}).shape,
        answers: z.object({
            // Background step
            expertise_years: ExperienceVettingBaseQuestion.extend({
                answer: z.number()
            }).optional(),
            online_profile_links: ExperienceVettingBaseQuestion.extend({
                answer: z.string().array()
            }).optional(),
            residential_country_region: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            residential_address: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            residential_address_extras: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            residential_state: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            residential_zip: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            hosting_as_business: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),

            // Merchandising step
            expertise_summary: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            recognition_summary: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            listing_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            byline: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            location_description: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            guest_activities: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            host_experience_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            improved_host_experience_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            host_expertise_description: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            improved_host_expertise_description: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            host_recognition_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            improved_host_recognition_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            experience_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            improved_experience_title: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            experience_description: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            improved_experience_description: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),

            // Location step
            country_region: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            location_address: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            location_address_extras: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            location_state: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            location_zip: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),
            meeting_point_name: ExperienceVettingBaseQuestion.extend({
                answer: z.string()
            }).optional(),

            // Pricing discount step
            guest_individual_price: ExperienceVettingBaseQuestion.extend({
                answer: z.number()
            }).optional(),
            group_minimum_price: ExperienceVettingBaseQuestion.extend({
                answer: z.number()
            }).optional(),
            instance_private_groups: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            new_listing_discount: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            early_bird_discount: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            maximum_public_group_size: ExperienceVettingBaseQuestion.extend({
                answer: z.number()
            }).optional(),

            // Legal step
            host_serve_alcohol: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            host_serve_food: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            host_transporting_guests_method: ExperienceVettingBaseQuestion.extend({
                answer: TransportingGuestsMode.array()
            }).optional(),
            host_transports_guests: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            involves_national_park: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            host_discusses_poi_significance: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            involves_point_of_interest: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            is_licensed_establishment: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional(),
            prepared_in_restaurant: ExperienceVettingBaseQuestion.extend({
                answer: z.boolean()
            }).optional()
        })
    }),
    itineraries: z.array(RemapItinerariesForViewOnly)
})
export type RemapExperienceForViewOnly = z.infer<typeof RemapExperienceForViewOnly>
